import {
  Box,
  Button,
  Checkbox,
  CloseIcon,
  FormControlLabel,
  IconButton,
  Link,
  Progress,
  Stack,
  Tile,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import React from "react"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import { Link as RouterLink, Redirect } from "react-router-dom"
import { DbgPaths } from "#/utils/Paths"
import {
  RoutingFlowLayoutBase,
  CloseButtonReactRouterLink
} from "#/components/carrot-plans/shared/RoutingFlowContainer"
import { useIsFreedomFertility } from "#/components/consent/hooks/useIsFreedomFertility"
import { useSubmitFreedomFertilityConsent } from "#/components/consent/hooks/useSubmitFreedomFertilityConsent"
import { ErrorPage } from "#/components/views/app/ErrorPage"

const consentCheckboxLabel = defineMessage({ defaultMessage: "Yes, I give my consent." })

export function FreedomFertilityConsent() {
  const { isFreedomFertility, isLoading } = useIsFreedomFertility()

  if (isLoading) {
    return (
      <RoutingFlowLayoutBase>
        <Progress />
      </RoutingFlowLayoutBase>
    )
  }

  if (!isFreedomFertility) {
    return <Redirect to="/" />
  }

  return (
    <RoutingFlowLayoutBase>
      <FreedomFertilityConsentForm />
    </RoutingFlowLayoutBase>
  )
}

const FreedomFertilityConsentForm = () => {
  const intl = useIntl()
  const [consentIsChecked, setConsentIsChecked] = React.useState(false)
  const [hasSubmitted, setHasSubmitted] = React.useState(false)
  const [hasError, setHasError] = React.useState(false)
  const { mutateAsync: submitFreedomFertilityConsent, isLoading: isSubmitting } = useSubmitFreedomFertilityConsent()

  async function handleConsentResponse(optIn: boolean): Promise<void> {
    try {
      await submitFreedomFertilityConsent({ optIn })
      setHasSubmitted(true)
    } catch (error) {
      setHasError(true)
    }
  }

  if (hasSubmitted) {
    return <SuccessMessage />
  }

  if (hasError) {
    return (
      <>
        <CloseButtonWithWrapper />
        <ErrorPage />
      </>
    )
  }

  return (
    <>
      <CloseButtonWithWrapper />
      <Stack>
        <ConsentContent />
        <FormControlLabel
          sx={{ marginBlockStart: (theme) => theme.spacing(theme.tokens.spacing.lg) }}
          control={<Checkbox checked={consentIsChecked} onChange={(e) => setConsentIsChecked(e.target.checked)} />}
          label={intl.formatMessage(consentCheckboxLabel)}
        />
        <ConsentButtons
          handleConsentResponse={handleConsentResponse}
          isLoading={isSubmitting}
          consentIsChecked={consentIsChecked}
        />
      </Stack>
    </>
  )
}

const ConsentContent = () => {
  return (
    <Stack gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
      <Tile color="default">
        <Typography>
          <FormattedMessage
            defaultMessage="Freedom Fertility is Carrot's pharmacy partner for fertility medications. Allowing Freedom Fertility to share your protected health information with Carrot will speed up the reimbursement process once you submit your reimbursement request. Learn more about Carrot Rx in your {benefitGuideLink}."
            values={{
              benefitGuideLink: (
                <Link
                  href={DbgPaths.CARROT_RX}
                  target="_blank"
                  sx={{ color: "inherit", textDecorationColor: "inherit" }}
                >
                  <FormattedMessage defaultMessage="benefit guide" />
                </Link>
              )
            }}
          />
        </Typography>
      </Tile>
      <Typography variant="h3" component="h2">
        <FormattedMessage defaultMessage="Consent for Freedom Fertility to share your protected health information with Carrot" />
      </Typography>
      <Typography>
        <FormattedMessage defaultMessage="I authorize Freedom Fertility to disclose my protected health information (PHI), such as my medication history, to Carrot, for Carrot to administer my Carrot Rx benefits, including more efficient expense processing." />
      </Typography>
      <Typography>
        <FormattedMessage defaultMessage="I understand that providing consent is voluntary and that I may withdraw my consent at any time, with effect going forward, by contacting info@get-carrot.com. I understand that my refusal or revocation will not affect treatment, payment, enrollment in a health plan, or eligibility for benefits." />
      </Typography>
      <Typography>
        <FormattedMessage defaultMessage="This authorization will expire one year from now, unless I provide a written notice of revocation to Carrot on behalf of Freedom Fertility earlier by email at info@get-carrot.com. The revocation will take effect as soon as Carrot receives my notice, but will not apply to any action taken by Carrot or Freedom Fertility before Carrot receives my notice." />
      </Typography>
      <Typography>
        <FormattedMessage defaultMessage="I understand that I have a right to receive a copy of this authorization by contacting info@get-carrot.com." />
      </Typography>
      <Typography>
        <FormattedMessage defaultMessage="By giving consent, I confirm that I have read and understood the terms of this authorization. I agree to Freedom Fertility using or disclosing my health information in the manner described above." />
      </Typography>
    </Stack>
  )
}

const ConsentButtons = ({
  handleConsentResponse,
  isLoading,
  consentIsChecked
}: {
  handleConsentResponse: (optIn: boolean) => void
  isLoading: boolean
  consentIsChecked: boolean
}) => {
  const theme = useTheme()

  return (
    <Stack
      marginTop={theme.spacing(theme.tokens.spacing.lg)}
      spacing={theme.spacing(theme.tokens.spacing.sm)}
      sx={{ display: "inline-flex", flexDirection: "row-reverse" }}
    >
      <Button
        color="secondary"
        disabled={isLoading || !consentIsChecked}
        variant="contained"
        onClick={() => handleConsentResponse(true)}
      >
        <FormattedMessage defaultMessage="Submit" />
      </Button>
      <Button
        color="secondary"
        disabled={isLoading || consentIsChecked}
        variant="outlined"
        onClick={() => handleConsentResponse(false)}
      >
        <FormattedMessage defaultMessage="Decline" />
      </Button>
    </Stack>
  )
}

const SuccessMessage = () => {
  const theme = useTheme()

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)} margin={theme.spacing(theme.tokens.spacing.lg)}>
      <Typography variant="h3" component="h2" color="inherit">
        <FormattedMessage defaultMessage="Your response has been recorded." />
      </Typography>
      <Box textAlign="end">
        <Button variant="contained" color="primary" component={RouterLink} to="/">
          <FormattedMessage defaultMessage="Go to Carrot home" />
        </Button>
      </Box>
    </Stack>
  )
}

const CloseButtonWithWrapper = () => {
  const intl = useIntl()

  return (
    <Box display="flex" justifyContent="flex-end">
      <IconButton
        data-testid="close-button"
        aria-label={intl.formatMessage({ defaultMessage: "Close" })}
        LinkComponent={CloseButtonReactRouterLink}
        href="/"
      >
        <CloseIcon />
      </IconButton>
    </Box>
  )
}
